import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { resetPassword } from '../../lib/api/auth';
import { errorMessage } from '../../lib/errors';
import { PASSWORD_MIN_LENGTH, passwordSchema } from '../../lib/password';
import { Button, Notice } from '../../components/primitives';
import { PasswordField } from '../../components/form';
import { PasswordStrengthMeter } from '../../components/PasswordStrength';
import { AuthShell } from './AuthShell';

const schema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Repita la nueva contraseña.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Las contraseñas no coinciden.',
  });

type ResetForm = z.infer<typeof schema>;

export function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (values: ResetForm): Promise<void> => {
    setFormError(null);
    try {
      await resetPassword({ token, newPassword: values.newPassword });
      setDone(true);
    } catch (error) {
      setFormError(errorMessage(error));
    }
  };

  if (token.length === 0) {
    return (
      <AuthShell title="Restablecer contraseña" subtitle="El enlace utilizado no es válido.">
        <div className="stack">
          <Notice tone="error" role="alert">
            El enlace no incluye un código de restablecimiento, o ya fue utilizado. Solicite uno nuevo.
          </Notice>
          <Link className="btn btn--block" to="/recuperar-password">
            Solicitar un enlace nuevo
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Restablecer contraseña"
      subtitle="Defina la contraseña con la que ingresará a partir de ahora."
    >
      {done ? (
        <div className="stack">
          <Notice tone="info" role="status">
            La contraseña se actualizó correctamente. Ya puede iniciar sesión.
          </Notice>
          <Link className="btn btn--primary btn--block" to="/login">
            Ir al inicio de sesión
          </Link>
        </div>
      ) : (
        <form className="auth__form" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
          {formError !== null && (
            <Notice tone="error" role="alert">
              {formError}
            </Notice>
          )}

          <PasswordField
            label="Nueva contraseña"
            autoComplete="new-password"
            autoFocus
            required
            hint={`Mínimo ${PASSWORD_MIN_LENGTH} caracteres.`}
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />

          <PasswordStrengthMeter value={newPassword} />

          <PasswordField
            label="Repetir nueva contraseña"
            autoComplete="new-password"
            required
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button type="submit" variant="primary" block loading={isSubmitting}>
            {isSubmitting ? 'Guardando…' : 'Guardar contraseña'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
